import * as React from 'react';
import styled from 'styled-components';

import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { ListView } from '@longline/aqua-ui/controls/ListView';
import { Message } from '@longline/aqua-ui/containers/Message';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { Pane } from '../../ui/Pane';
import { useSpeciesContext } from './ISpeciesContext';
import { ISpecies } from '../../api/models/ISpecies';
import { SpeciesInfoBox } from './SpeciesInfoBox';
import { hasFilter, ITextFilter, TextFilter } from '../../filters/TextFilter';

const SpeciesList= () => {
  const ctx = useSpeciesContext();
  const globalState = useGlobalState();
  const [filter, setFilter] = React.useState<ITextFilter>({});

  const renderNoData = () => {
    if(globalState.speciesReady == 'failed') {
      return (
        <Message.Error onRetry={globalState.loadSpecies}>
          Species failed to load.
        </Message.Error>
      );
    } else if (hasFilter(filter)) {
      return (
        <Message.NoFilteredData margin onClear={() => setFilter({})}>
          No species found. Your query may be too restrictive.
        </Message.NoFilteredData>
      )
    } else {
      return (
        <Message.NoData>
          There are no species yet.
        </Message.NoData>
      )
    }
  }    

  // Filter species by Q. This is memoized for speed.
  const filteredSpecies = React.useMemo(() => {
    return globalState.species.filter(s => 
      s.name.toLocaleLowerCase().includes((filter.q || "").toLocaleLowerCase())
    );
  }, [globalState.species, filter]);

  return (
    <>
      <Pane.Heading>
        <TextFilter filter={filter} onChange={setFilter}/>
      </Pane.Heading>
      <Pane.Subheader 
        action={<SecondaryButton title="Add" positive icon={SVG.Icons.Plus} onClick={() => ctx.setMode('create')}/>} 
        expandable
      >
        Species
      </Pane.Subheader>

      {/* Species list */}
      <ListView 
        dark 
        data={filteredSpecies} 
        onClick={ctx.setSpecies} 
        shadow
        ghost={globalState.speciesReady == 'loading'}
        active={ctx.species}
        aux={globalState.mode == 'species' ? <SpeciesInfoBox onView={() => ctx.setMode('view')}/> : undefined}
        total
        hover
        singular="species"
        plural="species"
        noData={renderNoData()}
        rowHeight={56}
        virtual
      >
        <ListView.Column paddingLeft={32} paddingRight={32} active width={3} name="Name" defaultSort sort="name">{(item: ISpecies) => item.name}</ListView.Column>
        {/* A null modelCode means AquaFish/AquaShell has no model for the species,
            so it cannot be simulated or selected for a project. */}
        <ListView.Column paddingRight={32} active width="120px" name="Model" align="right" sort="modelCode">
          {(item: ISpecies) => item.modelCode ?? <NoModel>&mdash;</NoModel>}
        </ListView.Column>
      </ListView>
    </>
  )
}

const NoModel = styled.span`
  color: ${p => p.theme.colors.neutral[50]};
`

export { SpeciesList }
